import {createContext, type PropsWithChildren, use, useCallback, useMemo} from "react";
import {useQuery, useQueryClient} from "@tanstack/react-query";
import {dataRetryPolicy, fetchBackend} from "@/lib/backend";
import {useSession} from "@/lib/auth/auth";
import {Group} from "@/lib/types";
import {useGlobalSearchParams} from "expo-router";

const GroupContext = createContext<{
    group?: Group;
    groupLoading: boolean;
    groupError: Error | null;
    refreshGroup: () => any;
}>({
    group: undefined,
    groupLoading: true,
    groupError: null,
    refreshGroup: () => {
    },
});

export function useGroup() {
    const value = use(GroupContext);
    if (!value) {
        throw new Error('useGroup must be wrapped in a <GroupProvider />');
    }
    return value;
}

export function GroupProvider({children}: PropsWithChildren) {
    const queryClient = useQueryClient();
    const {session, ...sessionParams} = useSession();

    const {group_id} = useGlobalSearchParams();
    const queryKey = useMemo(() => ['group', group_id, session], [group_id, session]);
    const queryKeyAll = useMemo(() => ['group', session], [session]);

    const refreshGroup = useCallback(async () => {
        await queryClient.invalidateQueries({queryKey});
        await queryClient.invalidateQueries({queryKey: queryKeyAll});
    }, [queryClient, queryKey, queryKeyAll]);

    const {data, isLoading, error} = useQuery<Group>({
        queryKey,
        queryFn: () => fetchBackend(`/group/${group_id}`, "GET", session),
        staleTime: 1000 * 60 * 15,
        retry: dataRetryPolicy,
        enabled: !!group_id && !sessionParams.isLoading,
    })

    return (
        <GroupContext.Provider
            value={{
                group: data,
                refreshGroup,
                groupLoading: isLoading,
                groupError: error,
            }}>
            {children}
        </GroupContext.Provider>
    );
}